// IndexChart.tsx
import React, { useMemo, useState } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import {
  Box,
  Select,
  MenuItem,
  CircularProgress,
  Typography,
  Tooltip,
  IconButton,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { ChartResponse } from 'src/api/Api';

interface IndexChartProps {
  chartData: ChartResponse | null;
  deploymentName: string;
  allowExporting?: boolean;
}

const IndexChart: React.FC<IndexChartProps> = ({ chartData, deploymentName, allowExporting = false }) => {
  const [selectedIndex, setSelectedIndex] = useState<string>('all');
  const [loading, setLoading] = useState(false);

  const options = useMemo(() => {
    if (!chartData) return null;
  
    const indices = ['aci', 'bi', 'adi', 'aei', 'ndsi'] as const;
    const colors = ['#1f77b4', '#ff7f0e', '#2ca02c', '#d62728', '#9467bd'];
    const symbols: Highcharts.SymbolKeyValue[] = [
      'circle',
      'diamond',
      'square',
      'triangle',
      'triangle-down',
    ];
  
    const series =
      selectedIndex === 'all'
        ? (() => {
            // For "all" view, normalize each index independently to preserve their ranges
            return indices.map((idx, i) => {
              const indexData = chartData.comparison[0]?.indices[idx] || [];
              const filteredValues = indexData.filter(
                (v): v is number => v !== null && v !== undefined
              );
              
              if (filteredValues.length === 0) {
                return {
                  type: 'line',
                  name: idx.toUpperCase(),
                  data: [],
                  color: colors[i],
                  marker: { symbol: symbols[i] },
                };
              }

              const index_min = Math.min(...filteredValues);
              const index_max = Math.max(...filteredValues);

              const normalizeIndex = (value: number | null | undefined) => {
                if (value === null || value === undefined) return 0;
                if (index_max === index_min) return 0;
                return (value - index_min) / (index_max - index_min);
              };

              return {
                type: 'line',
                name: idx.toUpperCase(),
                data: indexData.map(normalizeIndex),
                color: colors[i],
                marker: { symbol: symbols[i] },
              };
            });
          })()
        : (() => {
            // For individual indices, show raw/original values
            const indexData = chartData.comparison[0]?.indices[selectedIndex] || [];
            
            return [{
              type: 'line',
              name: selectedIndex.toUpperCase(),
              data: indexData.map((v: number | null | undefined) => v ?? 0),
              color: '#1f77b4',
              marker: { symbol: 'circle' },
            }];
          })();
  
    return {
      chart: { type: 'line', zoomType: 'x' },
      // ensure export buttons follow permission
      exporting: { enabled: allowExporting },
      title: { text: 'Index wise Activity' },
      xAxis: {
        categories: chartData.time_slots,
        labels: { rotation: -45 },
        title: { text: 'Time of Day' },
      },
      yAxis: {
        title: {
          text: selectedIndex === 'all' 
            ? 'Normalized Value (0–1)' 
            : 'Acoustic Index Value',
        },
      },
      tooltip: {
        pointFormatter: function (this: Highcharts.Point) {
          const formattedValue = selectedIndex === 'all'
            ? (this.y as number).toFixed(3)
            : (this.y as number).toFixed(2);
          return `<span style="color:${this.color}">●</span> ${this.series.name}: <b>${formattedValue}</b><br/>`;
        },
      },
      series,
    };
  }, [chartData, selectedIndex, allowExporting]);
  

  return (
    <Box sx={{ height: '500px', p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        {/* Deployment Name */}
        <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
          {deploymentName}
        </Typography>

        {/* Index Dropdown */}
        <Box display="flex" alignItems="center">
          <Select
            variant="standard"
            disableUnderline
            size="small"
            value={selectedIndex}
            onChange={(e) => {
              setLoading(true);
              setSelectedIndex(e.target.value);
              setTimeout(() => setLoading(false), 300);
            }}
            sx={{
              borderBottom: '2px solid',
              borderColor: 'primary.main',
              borderRadius: 0,
              mr: 1,
              '& .MuiSelect-select': {
                py: 0.5,
              },
              '& fieldset': {
                border: 'none',
              },
              '&:hover': {
                borderColor: 'primary.dark',
              },
              '&.Mui-focused': {
                borderColor: 'primary.main',
                borderBottomWidth: '2px',
              },
            }}
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="aci">ACI</MenuItem>
            <MenuItem value="bi">BI</MenuItem>
            <MenuItem value="adi">ADI</MenuItem>
            <MenuItem value="aei">AEI</MenuItem>
            <MenuItem value="ndsi">NDSI</MenuItem>
          </Select>

          <Tooltip
            title={
              <Box>
                <Typography variant="body2">
                  <b>ACI</b> – Acoustic Complexity Index
                </Typography>
                <Typography variant="body2">
                  <b>BI</b> – Bioacoustic Index
                </Typography>
                <Typography variant="body2">
                  <b>ADI</b> – Acoustic Diversity Index
                </Typography>
                <Typography variant="body2">
                  <b>AEI</b> – Acoustic Evenness Index
                </Typography>
                <Typography variant="body2">
                  <b>NDSI</b> – Normalized Difference Soundscape Index
                </Typography>
              </Box>
            }
            placement="right"
            arrow
          >
            <IconButton size="small">
              <InfoOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {loading ? (
        <Box
          sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '90%' }}
        >
          <CircularProgress size={32} />
          <Typography sx={{ ml: 1 }}>Updating index chart...</Typography>
        </Box>
      ) : (
        <HighchartsReact highcharts={Highcharts} options={options as any} />
      )}
    </Box>
  );
};

export default IndexChart;
